import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { API_URL } from '../config/api';

const GalleryPage = () => {
    const [galleryImages, setGalleryImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        fetchGallery();
    }, []);

    const fetchGallery = async () => {
        try {
            const response = await fetch(`${API_URL}/gallery?_=${Date.now()}`);
            
            if (!response.ok) {
                throw new Error('Gallery API error: ' + response.status);
            }
            
            const data = await response.json();
            // Map to URL format - include ALL images from API
            const images = Array.isArray(data) ? data.map(img => img.url) : [];
            setGalleryImages(images);
            console.log('✅ Site gallery loaded:', images.length, 'images');
        } catch (err) {
            console.error('Error fetching gallery:', err);
            // Fallback to default images
            setGalleryImages([
                '/gallery/2046030901.jpg',
                '/gallery/2050424694.jpg',
                '/gallery/2105620173.jpg',
                '/gallery/2107131574.jpg',
                '/gallery/2170730226.jpg',
                '/gallery/2174931569.jpg',
                '/gallery/2219928384.jpg',
                '/gallery/2244530033.jpg',
                '/gallery/2280126468.jpg',
                '/gallery/2293029886.jpg',
                '/gallery/2351729842.jpg',
                '/gallery/2353329841.jpg',
                '/gallery/2371220039.jpg',
                '/gallery/2373424190.jpg',
                '/gallery/2459730470.jpg',
                '/gallery/2470228331.jpg',
                '/gallery/2506631841.jpg',
                '/gallery/2607630724.jpg',
                '/gallery/2632328398.jpg',
                '/gallery/2725721399.jpg',
                '/gallery/2745631858.jpg',
                '/gallery/2759522331.jpg',
                '/gallery/2816027175.jpg',
                '/gallery/2841523640.jpg',
                '/gallery/2848629464.jpg',
                '/gallery/2874921060.jpg',
                '/gallery/2933020471.jpg',
                '/gallery/2940327377.jpg',
                '/gallery/3064228505.jpg',
                '/gallery/3111724699.jpg',
            ]);
        } finally {
            setLoading(false);
        }
    };

    const openLightbox = (index) => {
        setCurrentIndex(index);
        setSelectedImage(galleryImages[index]);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setSelectedImage(null);
        document.body.style.overflow = 'auto';
    };

    const showPrevious = (e) => {
        e?.stopPropagation();
        const newIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
        setSelectedImage(galleryImages[newIndex]);
    };

    const showNext = (e) => {
        e?.stopPropagation();
        const newIndex = currentIndex === galleryImages.length - 1 ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);
        setSelectedImage(galleryImages[newIndex]);
    };

    // Handle keyboard navigation
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (!selectedImage) return;
            if (e.key === 'ArrowLeft') showPrevious();
            if (e.key === 'ArrowRight') showNext();
            if (e.key === 'Escape') closeLightbox();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImage, currentIndex]);

    return (
        <div className="min-h-screen bg-gray-50">
            <SEO
                title="Galeri | Kırıkkale Far"
                description="Kırıkkale Far galerisi. Yaptığımız far ve stop tamir işlerinden görüntüler. Önceki işlerimizi inceleyin."
                keywords="far galeri, stop tamiri galeri, ankara far servisi, far tamir örnekleri"
                url="https://kirikkalefar.com/galeri"
            />
            <Header />
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">Galeri</h1>
                    <p className="text-xl text-blue-100">
                        Kırıkkale Far olarak yaptığımız işlerden bazı görüntüler
                    </p>
                </div>
            </div>

            {/* Gallery Grid */}
            <div className="container mx-auto px-4 py-12">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {galleryImages.map((image, index) => (
                        <div
                            key={index}
                            className="relative group overflow-hidden rounded-lg shadow-md cursor-pointer aspect-square"
                            onClick={() => openLightbox(index)}
                        >
                            <img
                                src={image}
                                alt={`Galeri ${index + 1}`}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                loading="lazy"
                            />
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                                <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Lightbox */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
                    onClick={closeLightbox}
                >
                    {/* Close Button */}
                    <button
                        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
                        onClick={closeLightbox}
                    >
                        <X size={40} />
                    </button>

                    {/* Previous Button */}
                    <button
                        className="absolute left-4 text-white hover:text-gray-300 transition-colors p-2"
                        onClick={showPrevious}
                    >
                        <ChevronLeft size={48} />
                    </button>

                    {/* Next Button */}
                    <button
                        className="absolute right-4 text-white hover:text-gray-300 transition-colors p-2"
                        onClick={showNext}
                    >
                        <ChevronRight size={48} />
                    </button>

                    {/* Main Image */}
                    <img
                        src={selectedImage}
                        alt={`Galeri ${currentIndex + 1}`}
                        className="max-w-[90vw] max-h-[85vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />

                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-lg">
                        {currentIndex + 1} / {galleryImages.length}
                    </div>
                </div>
            )}
            <Footer />
        </div>
    );
};

export default GalleryPage;
